import React from 'react';
import { ClubBackPill, PillButton, RingTab, MAGENTA, YELLOW, CHIP, MUTED } from './clubUi';
import { ClubLogo, ClubPills, TrustLine } from './ClubCover';

/**
 * The chrome around the club cover (README §1): the top bar (← CLUBS + the admin pills), the
 * PREVIEWING AS MEMBER and REQUESTED bands, the CTA row from `clubViewerState.ctas`, the tab
 * pills, and on phones the identity card + 3-up stats that sit under the 220px photo cover.
 */
export function ClubTopBar({ state, previewAsMember, onTogglePreview, onAction, phone = false }) {
  const h = phone ? 36 : 40;
  const px = phone ? '0 14px' : '0 18px';
  return (
    <div className="flex flex-wrap items-center justify-between font-mono" style={{ gap: 10 }} data-testid="club-top-bar">
      <ClubBackPill to="/clubs" size={h}>CLUBS</ClubBackPill>
      {state.adminBar && (
        <div className="flex flex-wrap items-center" style={{ gap: 8 }} data-testid="admin-bar">
          <PillButton variant={previewAsMember ? 'yellow' : 'ghost'} size={h} padding={px} fontSize={11} weight={700} onClick={onTogglePreview} aria-pressed={previewAsMember}>{previewAsMember ? 'EXIT PREVIEW' : 'PREVIEW AS MEMBER'}</PillButton>
          {!previewAsMember && <PillButton variant="ghost" size={h} padding={px} fontSize={11} weight={700} onClick={() => onAction('invite')}>INVITE</PillButton>}
          {!previewAsMember && <PillButton size={h} padding={px} fontSize={11} tracking=".16em" onClick={() => onAction('manage')} data-testid="manage-club">MANAGE</PillButton>}
        </div>
      )}
    </div>
  );
}

export function PreviewBand({ onExit }) {
  return (
    <div className="flex flex-wrap items-center justify-between font-mono" style={{ gap: 10, padding: '12px 12px 12px 20px', borderRadius: 22, background: YELLOW }} data-testid="preview-banner">
      <span className="uppercase" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em' }}>PREVIEWING AS MEMBER · ADMIN CONTROLS ARE HIDDEN</span>
      <PillButton size={34} fontSize={10} onClick={onExit}>EXIT PREVIEW</PillButton>
    </div>
  );
}

export function PendingBand({ band }) {
  if (!band) return null;
  return (
    <div className="flex flex-wrap items-center font-mono" style={{ gap: '10px 14px', padding: '12px 20px 12px 12px', borderRadius: 22, background: YELLOW }} data-testid="club-band">
      <span className="rounded-full uppercase" style={{ padding: '6px 12px', background: '#000', color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.16em' }}>{band.label}</span>
      <span style={{ fontSize: 13 }}>{band.value}.</span>
      {band.note && <span className="ml-auto uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>{band.note}</span>}
    </div>
  );
}

export function ClubCtas({ ctas, onAction, muted = false, phone = false }) {
  if (!ctas?.length) return null;
  return (
    <div className="flex items-stretch font-mono" style={{ gap: 8 }} data-testid="club-ctas">
      {ctas.map((c) => {
        const label = c.action === 'mute' ? (muted ? 'UNMUTE' : 'MUTE') : c.label;
        return (
          <PillButton key={c.label} variant={c.disabled ? 'disabled' : c.fill ? 'primary' : 'secondary'} size={phone ? 48 : 54} fontSize={phone ? 10 : 12} tracking=".12em" padding="0 14px" disabled={!!c.disabled}
            style={{ flex: '1 1 0', minWidth: 0, whiteSpace: 'normal', lineHeight: 1.25, textWrap: 'balance' }} className={c.disabled ? '' : 'hover:bg-[#FF00AA] hover:text-white'}
            onClick={() => !c.disabled && onAction?.(c.action)} data-testid={`cta-${c.label}`}>{label}</PillButton>
        );
      })}
    </div>
  );
}

/** HOME · POSTS · EVENTS · FORUM · MEMBERS · ABOUT as rainbow-ring pills; gated tabs grey with a lock. */
export function ClubTabs({ tabs, active, onChange, phone = false, testId }) {
  return (
    <div role="tablist" className="flex font-mono" style={{ flexWrap: phone ? 'nowrap' : 'wrap', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} data-testid={testId || (phone ? 'mobile-tab-strip' : 'club-tabs')}>
      {tabs.map((t) => {
        const on = t.key === active;
        const alert = !!t.alert && t.count;
        return (
          <RingTab key={t.key} role="tab" aria-selected={on} on={on} gated={!!t.gated} height={phone ? 36 : 40} onClick={() => onChange(t.key)} badge={t.count || undefined} badgeBg={alert ? MAGENTA : on ? '#fff' : CHIP} badgeFg={alert ? '#fff' : '#000'} data-testid={`tab-${t.key}`}>
            {t.label}
          </RingTab>
        );
      })}
    </div>
  );
}

/** The phone identity card under the photo cover: 72px logo, pills, 24px name, italic tagline, trust. */
export function ClubIdentityCard({ club, state }) {
  return (
    <section className="flex flex-col bg-white font-mono" style={{ borderRadius: 22, padding: '18px 16px', gap: 12 }} data-testid="club-mobile-header">
      <div className="flex items-center" style={{ gap: 14 }}>
        <ClubLogo club={club} size={72} radius={18} fontSize={30} />
        <span className="flex min-w-0 flex-col" style={{ gap: 8 }}>
          <ClubPills club={club} state={state} small />
          <h1 className="m-0" style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, textTransform: 'none', overflowWrap: 'anywhere' }}>{club.name}</h1>
        </span>
      </div>
      {club.tagline && <p className="m-0" style={{ fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic', fontSize: 18, lineHeight: 1.4 }}>{club.tagline}</p>}
      <TrustLine state={state} size={11} />
    </section>
  );
}

export function MobileStats({ stats, testId = 'club-mobile-stats' }) {
  return (
    <div className="grid overflow-hidden font-mono" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, gap: 4, borderRadius: 22 }} data-testid={testId}>
      {stats.map((s) => (
        <span key={s.k} className="flex flex-col items-center bg-white text-center" style={{ gap: 4, padding: '14px 6px' }}>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{s.n}</span>
          <span className="uppercase" style={{ fontSize: 8, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>{s.k}</span>
        </span>
      ))}
    </div>
  );
}
